/*
 * YourXI Bespoke XI: footer right block (personal message OR achievement + icon)
 * --------------------------------------------------------------------------
 * window.YXIFooter
 *
 *   mount(containerEl, {value, onChange(value)}) -> {update(value), getValue(), destroy()}
 *   renderSVG(value, {x, y, width, height, style, placeholder}) -> SVG string ('' when empty)
 *   normalize(value) -> a clean value object
 *   LIMITS, PLACEHOLDERS, DEFAULT
 *
 *   value = {
 *     mode:    'none' | 'message' | 'achievement',
 *     message: string            (120 chars, 4 lines max)
 *     lines:   [l1, l2, l3]      (32 / 24 / 32 chars)
 *     icon:    'none' | 'cup' | 'leagueTrophy' | 'shield' | 'medal' | 'football' | 'boot' | 'star' | 'promotion'
 *     finish:  'mono' | 'gold' | 'silver' | 'bronze'
 *   }
 *
 * Print typography follows the match-facts block on the real prints
 * (Newcastle 2025 Carabao Cup): Montserrat caps, centred, line 1 bold and
 * largest, line 2 bold and smaller, line 3 regular and smallest. The icon
 * sits centred above the text, the same way the shirt sits above the YOURXI
 * mark in the middle of the footer. Wrapping is balanced and never leaves a
 * single word alone on the last line.
 *
 * Optional deps (loaded before or after this file):
 *   kit-styles/icons.js   window.YourXIIcons  (icon art for picker + print)
 *   kit-styles/pitches.js window.YourXIPitches (getStyle for text colours)
 */
(function (root) {
  'use strict';

  var LIMITS = { message: 120, messageLines: 4, lines: [32, 24, 32] };
  var PLACEHOLDERS = {
    message: 'Happy 60th Dad. Love, Sam & Ella',
    lines: ['Sunday League Cup winners', '2024/25', 'Final: Hawthorn 3 - 1 Riverside']
  };
  var DEFAULT = { mode: 'message', message: '', lines: ['', '', ''], icon: 'none', finish: 'mono' };
  var FINISH_LIST = [['mono', 'Mono'], ['gold', 'Gold'], ['silver', 'Silver'], ['bronze', 'Bronze']];
  var ICON_FALLBACK = [
    { id: 'none', label: 'None' }, { id: 'cup', label: 'Cup' }, { id: 'leagueTrophy', label: 'League trophy' },
    { id: 'shield', label: 'Shield' }, { id: 'medal', label: 'Medal' }, { id: 'football', label: 'Football' },
    { id: 'boot', label: 'Boot' }, { id: 'star', label: 'Star' }, { id: 'promotion', label: 'Promotion' }
  ];
  var FONT = 'Montserrat';

  function Icons() { return root.YourXIIcons || null; }
  function iconList() { var I = Icons(); return (I && I.ICONS) || ICON_FALLBACK; }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function clean(s) { return String(s == null ? '' : s).replace(/[–—]/g, '-'); }

  function normalize(v) {
    v = v || {};
    var mode = v.mode === 'none' || v.mode === 'achievement' ? v.mode : 'message';
    var lines = Array.isArray(v.lines) ? v.lines.slice(0, 3) : [];
    while (lines.length < 3) lines.push('');
    lines = lines.map(function (l, i) { return clean(l).replace(/\n/g, ' ').slice(0, LIMITS.lines[i]); });
    var msg = clean(v.message).split('\n').slice(0, LIMITS.messageLines).join('\n').slice(0, LIMITS.message);
    var ids = iconList().map(function (i) { return i.id; });
    var icon = ids.indexOf(v.icon) >= 0 ? v.icon : 'none';
    var finish = /^(mono|gold|silver|bronze)$/.test(v.finish) ? v.finish : 'mono';
    return { mode: mode, message: msg, lines: lines, icon: icon, finish: finish };
  }

  /* ---------------------------------------------------------------------
     Text measuring. Canvas when we have a DOM (accurate once Montserrat is
     loaded), otherwise an average-width estimate for Montserrat caps.
     --------------------------------------------------------------------- */
  var ctx = null;
  function measurer(size, weight, tracking) {
    if (!ctx && typeof document !== 'undefined') {
      try { ctx = document.createElement('canvas').getContext('2d'); } catch (e) { ctx = null; }
    }
    return function (t) {
      var w;
      if (ctx) { ctx.font = weight + ' ' + size + 'px ' + FONT + ', sans-serif'; w = ctx.measureText(t).width; }
      else w = t.length * size * (weight >= 700 ? 0.68 : 0.62);
      return w + Math.max(0, t.length - 1) * tracking;
    };
  }

  function greedy(words, maxW, m) {
    var out = [], line = '';
    for (var i = 0; i < words.length; i++) {
      var t = line ? line + ' ' + words[i] : words[i];
      if (line && m(t) > maxW) { out.push(line); line = words[i]; } else line = t;
    }
    if (line) out.push(line);
    return out;
  }
  // balanced wrap: fewest lines, then the narrowest width that keeps that count, and no lone last word
  function wrapPara(text, maxW, m) {
    var words = text.split(' ').filter(Boolean);
    if (!words.length) return { lines: [], fits: true };
    var fits = words.every(function (w) { return m(w) <= maxW; });
    var base = greedy(words, maxW, m), n = base.length, best = base;
    if (n > 1) {
      var lo = 0, hi = maxW;
      // binary search the narrowest width that still gives n lines
      for (var k = 0; k < 18; k++) {
        var mid = (lo + hi) / 2, g = greedy(words, mid, m);
        var ok = g.length === n && g.every(function (l) { return m(l) <= maxW || l.indexOf(' ') < 0; });
        if (ok) { hi = mid; best = g; } else lo = mid;
      }
      // widow guard: pull a word down from the line above while it still fits
      if (words.length >= 3) {
        var L = best.slice();
        for (var tries = 0; tries < 3 && L[L.length - 1].indexOf(' ') < 0; tries++) {
          var prev = L[L.length - 2].split(' ');
          if (prev.length < 2) break;
          var moved = prev.pop() + ' ' + L[L.length - 1];
          if (m(moved) > maxW) break;
          L[L.length - 2] = prev.join(' '); L[L.length - 1] = moved;
        }
        best = L;
        // still a lone last word: report it so the renderer tries a slightly smaller size
        if (best.length > 1 && best[best.length - 1].indexOf(' ') < 0) fits = false;
      }
    }
    return { lines: best, fits: fits };
  }
  function wrap(text, maxW, m, maxLines) {
    var paras = String(text || '').toUpperCase().split(/\n+/).map(function (p) { return p.replace(/\s+/g, ' ').trim(); }).filter(Boolean);
    var lines = [], fits = true;
    paras.forEach(function (p) { var r = wrapPara(p, maxW, m); lines = lines.concat(r.lines); fits = fits && r.fits; });
    return { lines: lines, fits: fits && lines.length <= maxLines };
  }

  /* ---------------------------------------------------------------------
     Style: text colour and dark detection, from pitches.js when present
     --------------------------------------------------------------------- */
  var FALLBACK_STYLES = {
    classic: { paper: '#FFFFFF', textPrimary: '#111111', textSecondary: '#62625F' },
    night: { paper: '#141414', textPrimary: '#F5F3EE', textSecondary: '#A9A7A1' },
    grass: { paper: '#2F4A36', textPrimary: '#FFFFFF', textSecondary: '#CFDACB' },
    chalkboard: { paper: '#2A302E', textPrimary: '#F2F0E8', textSecondary: '#AEB3AD' }
  };
  function lum(hex) {
    var h = String(hex || '#ffffff').replace('#', '');
    if (h.length === 3) h = h.replace(/./g, '$&$&');
    var c = [0, 2, 4].map(function (i) { var v = parseInt(h.substr(i, 2), 16) / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }
  function resolveStyle(style) {
    var st;
    if (style && typeof style === 'object') st = style;
    else if (root.YourXIPitches && root.YourXIPitches.getStyle) st = root.YourXIPitches.getStyle(style || 'classic');
    else st = FALLBACK_STYLES[style] || FALLBACK_STYLES.classic;
    var paper = st.paper || '#FFFFFF';
    return { ink: st.textPrimary || '#111111', soft: st.textSecondary || st.textPrimary || '#62625F', dark: lum(paper) < 0.2 };
  }

  /* ---------------------------------------------------------------------
     Print renderer
     Sizes are for a 300-wide block on the 900 x 1200 print and scale with
     opts.width. Measured off the Newcastle 2025 print footer: line 1 is 14,
     line 2 is 10.75, line 3 is 8.5 print units (text widths / Montserrat advance).
     --------------------------------------------------------------------- */
  var TYPE = {
    l1: { size: 14, weight: 700, lh: 1.2, track: 0, max: 2 },
    l2: { size: 10.75, weight: 700, lh: 1.18, track: 0, max: 1 },
    l3: { size: 8.5, weight: 400, lh: 1.24, track: 0.2, max: 2 },
    msg: { size: 12, weight: 600, lh: 1.32, track: 0.2, max: 4 }
  };
  var GROUP_GAP = 6, ICON = 40, ICON_GAP = 9;

  function renderSVG(value, opts) {
    opts = opts || {};
    var v = normalize(value);
    if (v.mode === 'none') return '';
    var X = +opts.x || 0, Y = +opts.y || 0, W = +opts.width || 300, H = +opts.height || 160;
    var k = W / 300, S = resolveStyle(opts.style), ph = !!opts.placeholder;

    var groups; // [{spec, text}]
    if (v.mode === 'message') {
      var msg = v.message.trim() || (ph ? PLACEHOLDERS.message : '');
      groups = msg ? [{ spec: TYPE.msg, text: msg, ghost: !v.message.trim() }] : [];
    } else {
      groups = [];
      ['l1', 'l2', 'l3'].forEach(function (key, i) {
        var t = v.lines[i].trim(), ghost = false;
        if (!t && ph) { t = PLACEHOLDERS.lines[i]; ghost = true; }
        if (t) groups.push({ spec: TYPE[key], text: t, ghost: ghost });
      });
      if (ph && groups.every(function (g) { return g.ghost; }) === false) {
        groups = groups.filter(function (g) { return !g.ghost; }); // once they type, stop showing hints
      }
    }
    var icon = v.icon !== 'none' && Icons() ? v.icon : null;
    if (!groups.length && !icon) return '';

    // lay out at scale s, shrinking a little if anything overflows width, line count or height
    var lay = null;
    for (var s = 1; s >= 0.68; s -= 0.04) {
      var ok = true, h = 0, out = [];
      groups.forEach(function (g, gi) {
        var sp = g.spec, size = sp.size * k * s, m = measurer(size, sp.weight, sp.track * k * s);
        var r = wrap(g.text, W, m, sp.max);
        if (!r.fits) ok = false;
        var ls = r.lines.slice(0, sp.max);
        if (gi) h += GROUP_GAP * k * s;
        out.push({ g: g, size: size, lines: ls, lh: size * sp.lh, top: h });
        h += ls.length * size * sp.lh;
      });
      var iconSize = icon ? ICON * k * Math.max(s, 0.8) : 0;
      var total = h + (icon ? iconSize + (groups.length ? ICON_GAP * k * s : 0) : 0);
      lay = { out: out, textH: h, iconSize: iconSize, total: total };
      if (ok && total <= H) break;
    }

    var cx = X + W / 2, top = Y + (H - lay.total) / 2, svg = '';
    if (icon) {
      var art = Icons().iconSVG(icon, { finish: v.finish, size: lay.iconSize, onDark: S.dark });
      svg += art.replace(/^<svg/, '<svg x="' + r1(cx - lay.iconSize / 2) + '" y="' + r1(top) + '"');
      top += lay.iconSize + (groups.length ? ICON_GAP * k : 0);
    }
    lay.out.forEach(function (o) {
      var sp = o.g.spec, fill = o.g.ghost ? S.soft : S.ink, op = o.g.ghost ? ' opacity=".55"' : '';
      o.lines.forEach(function (t, i) {
        // caps sit optically centred in their line box: baseline at middle + 0.36em
        var base = top + o.top + o.lh * i + o.lh / 2 + o.size * 0.36;
        svg += '<text x="' + r1(cx) + '" y="' + r1(base) + '" text-anchor="middle" font-family="' + FONT + ', sans-serif" font-size="' + r1(o.size) +
          '" font-weight="' + sp.weight + '" letter-spacing="' + r1(sp.track * o.size / sp.size) + '" fill="' + fill + '"' + op + '>' + esc(t) + '</text>';
      });
    });
    return '<g class="yxi-footer-right" data-mode="' + v.mode + '">' + svg + '</g>';
  }
  function r1(n) { return Math.round(n * 10) / 10; }

  /* ---------------------------------------------------------------------
     Editor UI
     --------------------------------------------------------------------- */
  var uid = 0;
  function mount(el, opts) {
    opts = opts || {};
    var id = 'yxm' + (++uid);
    var state = normalize(opts.value || DEFAULT);
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};
    var dead = false;

    function emit() { if (!dead) onChange(JSON.parse(JSON.stringify(state))); }
    function counter(n, max) { return '<span class="yxm-count' + (n >= max ? ' is-full' : '') + '">' + n + '/' + max + '</span>'; }

    function iconGrid() {
      var I = Icons();
      return '<div class="yxm-field"><span class="yxm-label" id="' + id + '-il">Icon <span class="yxm-opt">(optional)</span></span>' +
        '<div class="yxm-icons" role="radiogroup" aria-labelledby="' + id + '-il">' +
        iconList().map(function (ic) {
          var art = I ? I.iconSVG(ic.id, { finish: state.finish, size: 40, placeholder: true }) : '';
          return '<button type="button" class="yxm-icon" role="radio" data-icon="' + ic.id + '" aria-checked="' + (state.icon === ic.id) + '">' +
            '<span class="yxm-icon-art">' + art + '</span><span class="yxm-icon-name">' + esc(ic.label) + '</span></button>';
        }).join('') + '</div>' +
        (state.icon !== 'none' ? '<span class="yxm-label yxm-sub" id="' + id + '-fl">Finish</span><div class="yxm-chips" role="radiogroup" aria-labelledby="' + id + '-fl">' +
          FINISH_LIST.map(function (f) {
            return '<button type="button" class="yxm-chip" role="radio" data-finish="' + f[0] + '" aria-checked="' + (state.finish === f[0]) + '">' +
              '<i class="yxm-swatch yxm-sw-' + f[0] + '"></i>' + f[1] + '</button>';
          }).join('') + '</div>' : '') + '</div>';
    }

    function fields() {
      if (state.mode === 'none') return '<p class="yxm-hint">The YOURXI mark sits centred with nothing beside it.</p>';
      if (state.mode === 'message') {
        return '<div class="yxm-field"><div class="yxm-row"><label class="yxm-label" for="' + id + '-msg">Your message</label>' + counter(state.message.length, LIMITS.message) + '</div>' +
          '<textarea class="yxm-input yxm-area" id="' + id + '-msg" data-f="message" maxlength="' + LIMITS.message + '" rows="3" placeholder="e.g. ' + esc(PLACEHOLDERS.message) + '">' + esc(state.message) + '</textarea>' +
          '<p class="yxm-hint">Up to 4 short lines, printed in capitals where the match facts sit on our prints.</p></div>' + iconGrid();
      }
      var labels = ['Main line', 'Season or date', 'Detail'], notes = ['Prints bold, largest', 'Prints bold, smaller', 'Prints regular, smallest'];
      return [0, 1, 2].map(function (i) {
        return '<div class="yxm-field"><div class="yxm-row"><label class="yxm-label" for="' + id + '-l' + i + '">' + labels[i] + ' <span class="yxm-opt">' + notes[i] + '</span></label>' +
          counter(state.lines[i].length, LIMITS.lines[i]) + '</div>' +
          '<input class="yxm-input" id="' + id + '-l' + i + '" data-f="l' + i + '" maxlength="' + LIMITS.lines[i] + '" autocomplete="off" placeholder="e.g. ' + esc(PLACEHOLDERS.lines[i]) + '" value="' + esc(state.lines[i]) + '"></div>';
      }).join('') + iconGrid();
    }

    function render() {
      var a = document.activeElement, f = a && el.contains(a) && a.dataset ? a.dataset.f : null;
      var sel = f && a.selectionStart != null ? [a.selectionStart, a.selectionEnd] : null;
      el.innerHTML = '<div class="yxm">' +
        '<span class="yxm-label" id="' + id + '-ml">Bottom right of your print</span>' +
        '<div class="yxm-seg" role="radiogroup" aria-labelledby="' + id + '-ml">' +
        [['none', 'None'], ['message', 'Message'], ['achievement', 'Achievement']].map(function (o) {
          return '<button type="button" role="radio" data-mode="' + o[0] + '" aria-checked="' + (state.mode === o[0]) + '">' + o[1] + '</button>';
        }).join('') + '</div><div class="yxm-body">' + fields() + '</div></div>';
      if (f) {
        var n = el.querySelector('[data-f="' + f + '"]');
        if (n) { n.focus(); if (sel) try { n.setSelectionRange(sel[0], sel[1]); } catch (e) {} }
      }
    }

    function setCount(input, n, max) {
      var c = input.closest('.yxm-field').querySelector('.yxm-count');
      if (c) { c.textContent = n + '/' + max; c.classList.toggle('is-full', n >= max); }
    }

    function onInput(e) {
      var t = e.target, f = t.dataset && t.dataset.f; if (!f) return;
      var v = clean(t.value);
      if (f === 'message') {
        var ls = v.split('\n');
        if (ls.length > LIMITS.messageLines) v = ls.slice(0, LIMITS.messageLines).join('\n');
        v = v.slice(0, LIMITS.message);
        if (v !== t.value) { var p = t.selectionStart; t.value = v; try { t.setSelectionRange(p, p); } catch (er) {} }
        state.message = v; setCount(t, v.length, LIMITS.message);
      } else {
        var i = +f.slice(1); v = v.slice(0, LIMITS.lines[i]);
        if (v !== t.value) t.value = v;
        state.lines[i] = v; setCount(t, v.length, LIMITS.lines[i]);
      }
      emit();
    }
    function onKey(e) { // block a 5th line in the message
      if (e.key === 'Enter' && e.target.dataset && e.target.dataset.f === 'message' && e.target.value.split('\n').length >= LIMITS.messageLines) e.preventDefault();
    }
    function onClick(e) {
      var b = e.target.closest && e.target.closest('button'); if (!b || !el.contains(b)) return;
      if (b.dataset.mode) { if (state.mode === b.dataset.mode) return; state.mode = b.dataset.mode; }
      else if (b.dataset.icon) state.icon = b.dataset.icon;
      else if (b.dataset.finish) state.finish = b.dataset.finish;
      else return;
      render(); emit();
      var again = el.querySelector('[data-mode="' + b.dataset.mode + '"],[data-icon="' + b.dataset.icon + '"],[data-finish="' + b.dataset.finish + '"]');
      if (again) again.focus();
    }
    function onArrow(e) { // arrow keys move within a radio group
      var b = e.target; if (!b.getAttribute || b.getAttribute('role') !== 'radio') return;
      var d = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key]; if (!d) return;
      var list = [].slice.call(b.parentNode.querySelectorAll('[role="radio"]')), n = list[(list.indexOf(b) + d + list.length) % list.length];
      e.preventDefault(); n.click();
    }

    el.addEventListener('input', onInput);
    el.addEventListener('keydown', onKey);
    el.addEventListener('keydown', onArrow);
    el.addEventListener('click', onClick);
    render();

    return {
      update: function (value) {
        var next = normalize(value);
        if (JSON.stringify(next) === JSON.stringify(state)) return;
        state = next; render();
      },
      getValue: function () { return JSON.parse(JSON.stringify(state)); },
      destroy: function () {
        dead = true;
        el.removeEventListener('input', onInput); el.removeEventListener('keydown', onKey);
        el.removeEventListener('keydown', onArrow); el.removeEventListener('click', onClick);
        el.innerHTML = '';
      }
    };
  }

  var api = { mount: mount, renderSVG: renderSVG, normalize: normalize, LIMITS: LIMITS, PLACEHOLDERS: PLACEHOLDERS, DEFAULT: DEFAULT, _wrap: wrap };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.YXIFooter = api;
})(this);
